import makeStyles from '@mui/styles/makeStyles';
import type { Theme } from '@p81-common/ui';

/**
 * Three-column top bar: breadcrumb | region | right-hand cluster.
 *
 * Both outer columns use `flex: 1 1 0` so the flex algorithm hands them equal
 * tracks, which puts the centre column on the true centre. `colEnd` also has a
 * min-size clamp, so when the cluster cannot fit its equal share the algorithm
 * freezes it at its intrinsic width and returns the surplus to `colStart`.
 *
 * The result is two regimes with no breakpoint and no measurement:
 *   wide   - region centred on the content area
 *   narrow - cluster at intrinsic width, region flush before it, breadcrumb
 *            absorbing all the pressure
 *
 * See the story for why the narrow regime is the norm in practice.
 */
const createClasses = makeStyles<Theme>(theme => ({
  header: {
    display: 'flex',
    width: '100%',
    height: '100%',
    minWidth: 0,
    alignItems: 'center',
    gap: theme.spacing(2)
  },

  // The only compressible column. Absorbs all pressure first.
  colStart: {
    display: 'flex',
    flex: '1 1 0',
    minWidth: 0,
    alignItems: 'center',
    // Stops a long trail painting over the region.
    overflow: 'hidden',
    '& nav': {
      marginRight: 0
    },
    // The breadcrumb measuring wrapper.
    '& > div': {
      minWidth: 0,
      overflow: 'hidden'
    },
    // BreadCrumbs sets data-fit. `hidden` keeps the element in the layout
    // instead of unmounting it, so the ResizeObserver keeps a target and can
    // still tell when the viewport grows back. `visibility` also removes it
    // from the accessibility tree and stops it being clickable, which
    // `opacity: 0` would not.
    '& > div[data-fit="hidden"]': {
      visibility: 'hidden'
    },
    // MuiBreadcrumbs-ol defaults to flex-wrap: wrap. In a fixed-height bar that
    // makes a long trail wrap onto extra lines and spill out below the bar
    // instead of collapsing, so it has to be pinned to a single row.
    '& .MuiBreadcrumbs-ol': {
      flexWrap: 'nowrap'
    },
    '& .MuiBreadcrumbs-li': {
      minWidth: 0,
      '& > *': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }
    },
    // MUI's collapsed Breadcrumbs expands the full trail when the ellipsis is
    // clicked, through internal state that defaultProps cannot reach or reset.
    // In a fixed-height bar the expanded trail just gets clipped and there is
    // no state change left to re-collapse it. Truncation is all that was asked
    // for here, so the ellipsis is decorative.
    '& .MuiBreadcrumbs-li button': {
      pointerEvents: 'none'
    }
  },

  colCenter: {
    display: 'flex',
    flex: '0 0 auto',
    alignItems: 'center',
    whiteSpace: 'nowrap'
  },

  // Same grow and basis as colStart, so the tracks are equal when there is
  // room. `flex-basis` must be 0, not auto: auto reintroduces the cluster's
  // width into the free-space calculation and shifts the region off centre.
  // `min-width` + `nowrap` make the cluster incompressible.
  colEnd: {
    display: 'flex',
    flex: '1 1 0',
    minWidth: 'max-content',
    alignItems: 'center',
    justifyContent: 'flex-end',
    overflow: 'visible',
    whiteSpace: 'nowrap'
  }
}));

export default createClasses;
